import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spinner, Typography } from "@material-tailwind/react";
import {
  createCustomersAgentProjectConfig,
} from "../../services/users";
import { useUser } from "../../context/UserContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import {
  ChevronDown,
  Edit,
  Plus,
  Search,
  Handshake,
  Layers,
  CheckCircle2,
  Building2,
  Mail,
  Phone,
  MessageSquare,
  MapPin,
  Download,
} from "lucide-react";
import { IconButton } from "../../components/Buttons";
import {
  fetchProjects,
  fetchPropertyConfigurations,
} from "../../services/projects";
import { FormProvider, useForm } from "react-hook-form";
import { FormModal, Field, FormSubmit, AddressFields, yupResolver } from "../../components/form";
import { agentSchema } from "../../services/users/schemas";
import usePrivilege from "../../hooks/usePrivilege";
import axiosInstance from "../../services/api/axios";
import BulkUploadAgents from "../../components/BulkUploadAgents";
import usePagination from "../../hooks/usePagination";
import { encryptForTransit, decryptFromTransit } from "../../utils/common";
import PaginationControls from "../../components/PaginationControls";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import SearchInput from "../../components/SearchInput";

const defaultValues = {
  companyName: "",
  ownerName: "",
  mobileNoWa: "",
  mobileNoCalling: "",
  emailId: "",
  reraNumber: "",
  emailPasswordSecret: "",
  address: {
    buildingNo: "",
    streetName1: "",
    streetName2: "",
    city: "",
    district: "",
    pinCode: "",
    state: "",
    country: "",
  },
  customerType: "AGENT",
  isActive: true,
  metaId: "",
  whatsappSecretAccessToken: "",
};

// Helper function to normalize configurations response to always be an array
const normalizeConfigurations = (configurations) => {
  if (!configurations) return [];
  if (Array.isArray(configurations)) return configurations;
  if (configurations?.items && Array.isArray(configurations.items)) return configurations.items;
  if (configurations?.data?.items && Array.isArray(configurations.data.items)) return configurations.data.items;
  if (configurations?.data && Array.isArray(configurations.data)) return configurations.data;
  return [];
};

function AgentList() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" or "edit"
  const [selectedProjectIds, setSelectedProjectIds] = useState([]); // Store Project IDs
  const [selectedAgentForEdit, setSelectedAgentForEdit] = useState(null);
  const [selectedProjectForEdit, setSelectedProjectForEdit] = useState("");
  const user = useUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [, setLoading] = useState(false);
  const { hasPrivilege } = usePrivilege();
  const [selectedProjectConfigs, setSelectedProjectConfigs] = useState({});
  const [availableConfigs, setAvailableConfigs] = useState({});
  const [configCache, setConfigCache] = useState({});
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  const [addPartnerDropdownOpen, setAddPartnerDropdownOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const addPartnerDropdownRef = useRef(null);
  // `methods` is kept whole so we can hand it to <FormProvider> below; the
  // individual helpers are also destructured for the call-sites outside the
  // form body that already use them (reset on close, setValue on edit init).
  const methods = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(agentSchema),
  });
  // `errors`/`register`/`Controller` are no longer destructured here — Field
  // pulls them via useFormContext under <FormProvider>. We still need
  // handleSubmit/setValue/reset/watch for things that happen *outside* the
  // form body (open/close, edit-mode init, isValid gating).
  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
  } = methods;

  const customerId = user?.userInfo?.loginIdentifierDetails?.customer?.customerId;

  const buildCustomerUpdateFormData = (agentData) => {
    const formData = new FormData();
    formData.append("company_name", agentData.companyName || "");
    formData.append("owner_name", agentData.ownerName || "");
    formData.append("mobile_no_wa", agentData.mobileNoWa || "");
    formData.append("mobile_no_calling", agentData.mobileNoCalling || "");
    formData.append("email_id", agentData.emailId || "");
    formData.append("email_password_secret", agentData.emailPasswordSecret || "");
    formData.append("customer_type", agentData.customerType || "AGENT");
    formData.append("rera_number", agentData.reraNumber || "");
    formData.append("is_active", agentData.isActive ?? true);
    formData.append("meta_id", agentData.metaId || "");
    formData.append(
      "whatsapp_secret_access_token",
      agentData.whatsappSecretAccessToken || ""
    );
    formData.append("building_no", agentData.address?.buildingNo || "");
    formData.append("street_name1", agentData.address?.streetName1 || "");
    formData.append("street_name2", agentData.address?.streetName2 || "");
    formData.append("city", agentData.address?.city || "");
    formData.append("district", agentData.address?.district || "");
    formData.append("pin_code", agentData.address?.pinCode || "");
    formData.append("state", agentData.address?.state || "");
    formData.append("country", agentData.address?.country || "");
    return formData;
  };

  // Create fetch function for agents with pagination
  const fetchAgentsBackend = useCallback(async ({ page, page_size, search }) => {
    const params = { page, page_size, customerId };
    if (search) params.search = search;

    try {
      const response = await axiosInstance.get('/customers/agent/project/configs', { params });
      
      // Handle both paginated and non-paginated responses
      let agentsArray;
      if (response?.items && response.total !== undefined) {
        agentsArray = response.items;
      } else if (Array.isArray(response)) {
        agentsArray = response;
      } else {
        agentsArray = [];
      }

      // Format agents to include property configs and projects
      const formattedAgents = agentsArray.map((item) => ({
        ...item.agent,
        propertyConfigs: item.propertyConfigs,
        projects: item.projects,
      }));

      // Return paginated response format
      if (response?.items && response.total !== undefined) {
        return {
          ...response,
          items: formattedAgents
        };
      } else {
        return {
          items: formattedAgents,
          total: formattedAgents.length,
          page: 1,
          page_size: formattedAgents.length,
          total_pages: 1,
          has_next: false,
          has_previous: false
        };
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      return { items: [], total: 0, page: 1, page_size: 30, total_pages: 1, has_next: false, has_previous: false };
    }
  }, [customerId]);

  // Use the pagination hook with manual search
  const {
    data: agents,
    pagination,
    searchInput,
    loading: paginationLoading,
    error,
    goToPage,
    changePageSize,
    handleSearchInputChange,
    triggerSearch,
    refetch
  } = usePagination(fetchAgentsBackend, 30, '', true, true);

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (!paginationLoading && agents.length > 0) {
      setIsInitialLoad(false);
    }
  }, [paginationLoading, agents.length]);

  // CSV export — builds the file client-side from the visible page's `agents`
  // array. We no longer render an AG-Grid (it was replaced by the card view),
  // so the old grid.api.exportDataAsCsv() path is gone; this keeps the Export
  // button working without re-introducing the grid.
  const handleExport = () => {
    if (!agents || agents.length === 0) {
      toast.info("No partners to export.");
      return;
    }
    const headers = [
      "Owner Name",
      "Company Name",
      "Email",
      "Phone",
      "WhatsApp",
      "City",
      "State",
      "Projects",
      "Configurations",
      "RERA",
      "Status",
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = agents.map((a) =>
      [
        a.ownerName,
        a.companyName,
        a.emailId,
        a.mobileNoCalling,
        a.mobileNoWa,
        a.address?.city,
        a.address?.state,
        (a.projects || []).map((p) => p.projectName).join("; "),
        (a.propertyConfigs || []).map((c) => c.configName).join("; "),
        a.reraNumber || a.reraNo || a.rera_number,
        a.isActive ? "Active" : "Inactive",
      ].map(escape).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `channel-partners-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjectsData = async () => {
      if (customerId) {
        try {
          const projectResponse = await fetchProjects({ customerId });
          if (projectResponse) {
            setProjects(projectResponse);
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      }
    };
    fetchProjectsData();
  }, [customerId]);


  const toggleAddPartnerDropdown = () => {
    setAddPartnerDropdownOpen(!addPartnerDropdownOpen);
  };

  const handleAddPartnerMethod = (method) => {
    setAddPartnerDropdownOpen(false);
    if (method === "manual") {
      openModal();
    } else if (method === "bulk") {
      setIsBulkUploadModalOpen(true);
    }
  };

  const closeBulkUploadModal = () => {
    setIsBulkUploadModalOpen(false);
  };

  const openModal = () => {
    reset(defaultValues);
    setFormMode("create");
    setIsFormModalOpen(true);
    setSelectedProjectIds([]); // Clear project selection on open
    setDropdownOpen(false);
    setSelectedProjectConfigs({});
    setConfigCache({}); // **NEW: Clear the config cache on open**
    setAvailableConfigs({}); //also clear AvailableConfig
  };

  const openEditModal = async (agent) => {
    setFormMode("edit");
    setSelectedAgentForEdit(agent);

    let agentDetails = agent;
    try {
      const customerResponse = await axiosInstance.get(
        `/customers?customerId=${agent.customerId}`
      );

      let customerData;
      if (customerResponse?.items && customerResponse.total !== undefined) {
        customerData = customerResponse.items[0] || customerResponse.items;
      } else if (Array.isArray(customerResponse)) {
        customerData = customerResponse[0];
      } else {
        customerData = customerResponse;
      }

      if (customerData) {
        agentDetails = {
          ...agent,
          ...customerData,
          // Prefer customer details when available, keep list data as fallback
          address: customerData.address || agent.address,
        };
      }
      
    } catch (error) {
      console.error("Error fetching full agent details for edit:", error);
    }


    reset({
      companyName: agentDetails.companyName || "",
      ownerName: agentDetails.ownerName || "",
      mobileNoWa: agentDetails.mobileNoWa || "",
      emailId: agentDetails.emailId || "",
      reraNumber:
        agentDetails.reraNumber ||
        agentDetails.reraNo ||
        agentDetails.rera_number ||
        "",
      emailPasswordSecret:
        decryptFromTransit(agentDetails.emailPasswordSecret) || "",
      address: {
        buildingNo: agentDetails.address?.buildingNo || "",
        streetName1: agentDetails.address?.streetName1 || "",
        streetName2: agentDetails.address?.streetName2 || "",
        city: agentDetails.address?.city || "",
        district: agentDetails.address?.district || "",
        pinCode: agentDetails.address?.pinCode || "",
        state: agentDetails.address?.state || "",
        country: agentDetails.address?.country || agentDetails.country || "",
      },
      customerType: "AGENT",
      isActive: agentDetails.isActive,
      metaId: agentDetails.metaId || "",
      whatsappSecretAccessToken:
        decryptFromTransit(agentDetails.whatsappSecretAccessToken) || "",
    });

    // Select the projects and configurations associated with the agent for editing
    const projectIds = agent.projects.map((project) => project.projectId);
    setSelectedProjectIds(projectIds);

    const initialSelectedConfigs = {};
    agent.propertyConfigs.forEach((config) => {
      const projectId = config.projectPhase.project.projectId;
      if (!initialSelectedConfigs[projectId]) {
        initialSelectedConfigs[projectId] = [];
      }
      initialSelectedConfigs[projectId].push(config.propertyConfigId);
    });

    setSelectedProjectConfigs(initialSelectedConfigs);

    if (agent.projects && agent.projects.length > 0) {
      setSelectedProjectForEdit(agent.projects[0].projectName);
    } else {
      setSelectedProjectForEdit("");
    }
    setIsFormModalOpen(true);
    setDropdownOpen(false);
    setConfigCache({});
    setAvailableConfigs({});

    projectIds.forEach(async (projectId) => {
      if (!configCache[projectId]) {
        setLoading(true);
        try {
          const configurations = await fetchPropertyConfigurations({
            projectId,
          });
          const normalizedConfigs = normalizeConfigurations(configurations);
          setConfigCache((prev) => ({ ...prev, [projectId]: normalizedConfigs }));
          setAvailableConfigs((prev) => ({
            ...prev,
            [projectId]: normalizedConfigs,
          }));

          
        } catch (error) {
          console.error(
            `Error fetching configurations for project ${projectId}:`,
            error
          );
          toast.error(
            `Error fetching configurations for project ${projectId}`
          );
          setConfigCache((prev) => ({ ...prev, [projectId]: [] }));
        } finally {
          setLoading(false);
        }
      } else {
        // Ensure cached configs are normalized
        const normalizedConfigs = normalizeConfigurations(configCache[projectId]);
        setAvailableConfigs((prev) => ({
          ...prev,
          [projectId]: normalizedConfigs,
        }));
      }
    });

    // Set MobileNoCalling after reset, ensuring it doesn't get overwritten by defaultValues
    setValue("mobileNoCalling", agentDetails.mobileNoCalling || "", {
      shouldValidate: false,
      shouldDirty: false,
    });
   
  };

  const closeModal = () => {
    reset(defaultValues);
    setIsFormModalOpen(false);
    setSelectedProjectIds([]); // Clear selected projects on close
    setDropdownOpen(false);
    setSelectedProjectConfigs({});
    setConfigCache({});
    setAvailableConfigs({});
  };

  const handleProjectSelect = async (projectId, isChecked) => {
    if (isChecked) {
      setSelectedProjectIds((prev) => [...prev, projectId]);

      if (!configCache[projectId]) {
        setLoading(true);
        try {
          const configurations = await fetchPropertyConfigurations({
            projectId,
          });
          const normalizedConfigs = normalizeConfigurations(configurations);
          setConfigCache((prev) => ({ ...prev, [projectId]: normalizedConfigs }));
          setAvailableConfigs((prev) => ({
            ...prev,
            [projectId]: normalizedConfigs,
          }));

          
        } catch (error) {
          console.error(
            `Error fetching configurations for project ${projectId}:`,
            error
          );
          toast.error(
            `Error fetching configurations for project ${projectId}`
          );
          setConfigCache((prev) => ({ ...prev, [projectId]: [] }));
        } finally {
          setLoading(false);
        }
      } else {
        // Ensure cached configs are normalized
        const normalizedConfigs = normalizeConfigurations(configCache[projectId]);
        setAvailableConfigs((prev) => ({
          ...prev,
          [projectId]: normalizedConfigs,
        }));
      }
    } else {
      setSelectedProjectIds((prev) => prev.filter((id) => id !== projectId));
      setAvailableConfigs((prev) => {
        const newConfigs = { ...prev };
        delete newConfigs[projectId];
        return newConfigs;
      });
    }

    setSelectedProjectConfigs((prev) => {
      const newConfigs = { ...prev };
      delete newConfigs[projectId];
      return newConfigs;
    });
  };
  const handleConfigSelect = (projectId, configId, isChecked) => {
    setSelectedProjectConfigs((prevState) => {
      const projectConfigs = prevState[projectId] || [];
      const newConfigs = isChecked
        ? [...projectConfigs, configId]
        : projectConfigs.filter((id) => id !== configId);

      return {
        ...prevState,
        [projectId]: newConfigs,
      };
    });
  };

  const handleAddAgentToProject = useCallback(
    async (values) => {
      try {
        if (!isValid) return;
        setLoading(true);

        // if (!selectedProjectIds || selectedProjectIds.length === 0) {
        //   console.error("No project selected");
        //   toast.warn("No project selected");
        //   setLoading(false);
        //   return;
        // }

        if (
          !user?.userInfo?.loginIdentifierDetails?.customer?.customerId
        ) {
          console.error(
            "Developer ID not found. User may not be properly authenticated."
          );
          toast.warn(
            "Developer ID not found. User may not be properly authenticated."
          );
          setLoading(false);
          return;
        }

        if (
          !values.ownerName ||
          !values.mobileNoCalling ||
          !values.emailId
        ) {
          toast.error(
            "Please fill in all required fields (Name, Phone, and Email)"
          );
          setLoading(false);
          return;
        }

        const developerId =
          user.userInfo.loginIdentifierDetails.customer.customerId;

        const agentData = {
          companyName: values.companyName,
          ownerName: values.ownerName,
          mobileNoWa: values.mobileNoWa,
          mobileNoCalling: values.mobileNoCalling,
          emailId: values.emailId,
          emailPasswordSecret: encryptForTransit(values.emailPasswordSecret),
          address: {
            buildingNo: values.address.buildingNo,
            streetName1: values.address.streetName1,
            streetName2: values.address.streetName2,
            city: values.address.city,
            district: values.address.district,
            pinCode: values.address.pinCode,
            state: values.address.state,
            country: values.address.country,
          },
          customerType: "AGENT",
          reraNumber: values.reraNumber,
          isActive: true,
          metaId: values.metaId ?? "",
          whatsappSecretAccessToken: encryptForTransit(values.whatsappSecretAccessToken ?? ""),
        };

        const allPropertyConfigIds = [];

        for (const projectId of selectedProjectIds) {
          const configIdsForProject = selectedProjectConfigs[projectId] || [];
          allPropertyConfigIds.push(...configIdsForProject);
        }

        const payload = {
          agentData: agentData,
          propertyConfigIds: allPropertyConfigIds,
          developerId: developerId,
          projectIds: selectedProjectIds, // Send all selected project IDs
        };
        console.log("[Agent Create] Form values:", values);
        console.log("[Agent Create] Payload:", payload);
        

        try {
          const createResponse = await createCustomersAgentProjectConfig(payload);

          const createdCustomerId =
            createResponse?.agent?.customerId ||
            createResponse?.customerId ||
            createResponse?.agentData?.customerId ||
            createResponse?.items?.[0]?.agent?.customerId;

          if (createdCustomerId) {
            const customerFormData = buildCustomerUpdateFormData(agentData);
            await axiosInstance.put(
              `/customers?customerId=${createdCustomerId}`,
              customerFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            console.log(
              "[Agent Create] Synced agent core fields via /customers:",
              createdCustomerId
            );
          } else {
            console.warn(
              "[Agent Create] Could not resolve created customerId for /customers sync",
              createResponse
            );
          }

          toast.success("Agent added successfully!");
          closeModal();
          refetch(); // Refresh the paginated data
        } catch (error) {
          console.error("Error adding agent:", error);
          toast.error(
            error?.response?.data?.message || "Error adding agent"
          );
        }
      } catch (error) {
        console.error("Error adding agent:", error);
        toast.error(error?.response?.data?.message || "Error adding agent");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isValid,
      projects,
      selectedProjectIds,
      user,
      selectedProjectConfigs,
      refetch,
    ]
  );

  const handleEditAgent = useCallback(
    async (values) => {
      try {
        setLoading(true);

        if (!selectedAgentForEdit) {
          console.error("No agent selected for edit");
          toast.warn("No agent selected for edit");
          setLoading(false);
          return;
        }
        if (
          !values.ownerName ||
          !values.mobileNoCalling ||
          !values.emailId
        ) {
          toast.error(
            "Please fill in all required fields (Name, Phone, and Email)"
          );
          setLoading(false);
          return;
        }

        const developerId =
          user?.userInfo?.loginIdentifierDetails?.customer?.customerId;

        const agentData = {
          companyName: values.companyName,
          ownerName: values.ownerName,
          mobileNoWa: values.mobileNoWa,
          mobileNoCalling: values.mobileNoCalling,
          emailId: values.emailId,
          emailPasswordSecret: encryptForTransit(values.emailPasswordSecret),
          address: {
            buildingNo: values.address.buildingNo,
            streetName1: values.address.streetName1,
            streetName2: values.address.streetName2,
            city: values.address.city,
            district: values.address.district,
            pinCode: values.address.pinCode,
            state: values.address.state,
            country: values.address.country,
          },
          customerType: "AGENT",
          reraNumber: values.reraNumber,
          isActive: values.isActive,
          metaId: values.metaId,
          whatsappSecretAccessToken: encryptForTransit(values.whatsappSecretAccessToken),
        };

        const allPropertyConfigIds = [];
        for (const projectId of selectedProjectIds) {
          const configIdsForProject = selectedProjectConfigs[projectId] || [];
          allPropertyConfigIds.push(...configIdsForProject);
        }
        const payload = {
          agentData: agentData,
          propertyConfigIds: allPropertyConfigIds,
          developerId: developerId,
          projectIds: selectedProjectIds, // Send all selected project IDs
        };
        console.log("[Agent Edit] Form values:", values);
        console.log("[Agent Edit] Payload:", payload);
        

        try {
          await axiosInstance.put(
            `/customers/agent/project/configs?agentId=${selectedAgentForEdit.customerId}`,
            payload
          );

          const customerFormData = buildCustomerUpdateFormData(agentData);
          await axiosInstance.put(
            `/customers?customerId=${selectedAgentForEdit.customerId}`,
            customerFormData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          console.log(
            "[Agent Edit] Synced agent core fields via /customers:",
            selectedAgentForEdit.customerId
          );

          toast.success("Agent updated successfully!");
          closeModal();
          refetch(); // Refresh the paginated data
        } catch (error) {
          console.error("Error updating agent:", error);
          toast.error(
            error?.response?.data?.message || "Error updating agent"
          );
        }
      } catch (error) {
        console.error("Error updating agent:", error);
        toast.error(error?.response?.data?.message || "Error updating agent");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      projects,
      selectedProjectForEdit,
      selectedAgentForEdit,
      isValid,
      selectedProjectIds,
      user,
      selectedProjectConfigs,
      refetch,
    ]
  );

  // Handle Click Outside - REVISED
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the dropdown is open
      if (dropdownOpen) {
        // Check if the click occurred outside the dropdown AND outside the modal content
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          modalRef.current &&
          modalRef.current.contains(event.target) // **AND within the modal**
        ) {
          setDropdownOpen(false);
        }
      }
    }

    // Add event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Handle Click Outside for Add Partner Dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (addPartnerDropdownOpen && addPartnerDropdownRef.current && !addPartnerDropdownRef.current.contains(event.target)) {
        setAddPartnerDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [addPartnerDropdownOpen]);

  // Page-level KPI numbers — computed from the current page rather than a new
  // BE endpoint. `pagination.total` covers portfolio-wide count; the rest is
  // a fair best-effort summary based on the visible page (good enough for an
  // at-a-glance band, exact metrics live on the per-row chips).
  const visibleActive = (agents || []).filter((a) => a.isActive).length;
  const visibleProjectsCount = new Set(
    (agents || []).flatMap((a) => (a.projects || []).map((p) => p.projectId))
  ).size;
  const visibleConfigsCount = new Set(
    (agents || []).flatMap((a) =>
      (a.propertyConfigs || []).map((c) => c.propertyConfigId)
    )
  ).size;

  const formatLocation = (addr) => {
    if (!addr) return "—";
    const parts = [addr.city, addr.state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {isFormModalOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-40"></div>
      )}

      {/* Hero band — slate gradient, embedded search + Add CTA */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <Handshake size={12} /> Network
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
              Channel partners
            </h1>
            <p className="text-xs text-slate-300 mt-1">
              {pagination?.total ?? 0} partner{(pagination?.total ?? 0) === 1 ? "" : "s"}
              {searchInput ? ` matching "${searchInput}"` : " in your network"}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") triggerSearch();
                }}
                placeholder="Search partners…"
                disabled={paginationLoading}
                className="pl-9 pr-3 py-2 rounded-lg bg-white/10 text-white text-sm placeholder-slate-400 border border-white/10 focus:bg-white/20 focus:border-white/30 focus:outline-none w-full sm:w-72"
              />
            </div>
            <div className="hidden">
              {/* SearchInput retained off-screen for any consumer watching it */}
              <SearchInput
                value={searchInput}
                onChange={handleSearchInputChange}
                onSearch={triggerSearch}
                disabled={paginationLoading}
              />
            </div>
            {hasPrivilege("canEdit") && (
              <div className="relative" ref={addPartnerDropdownRef}>
                <IconButton
                  onClick={toggleAddPartnerDropdown}
                  icon={<Plus className="mr-1.5 size-3.5" />}
                  left
                  className="!w-auto !px-3 !py-1.5 text-sm"
                >
                  Add
                </IconButton>
                {addPartnerDropdownOpen && (
                  <div className="absolute right-0 font-medium w-40 mt-2 shadow-lg rounded-xl border bg-white border-slate-200 z-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleAddPartnerMethod("manual")}
                      className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-flamingo-50 hover:text-flamingo-700 transition-colors border-b border-slate-100"
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddPartnerMethod("bulk")}
                      className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-flamingo-50 hover:text-flamingo-700 transition-colors"
                    >
                      Bulk
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {paginationLoading && isInitialLoad ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32"
            >
              <Skeleton circle height={40} width={40} />
              <Skeleton height={12} width="70%" className="mt-3" />
              <Skeleton height={28} width="50%" className="mt-2" />
            </div>
          ))
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
              <div className="w-10 h-10 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center">
                <Handshake size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                  Total partners
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {pagination?.total ?? 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">In your network</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                  Active on this page
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {visibleActive}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  of {agents?.length || 0} shown
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Building2 size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                  Projects covered
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {visibleProjectsCount}
                </p>
                <p className="text-xs text-slate-500 mt-1">Unique on this page</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <Layers size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                  Configurations
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {visibleConfigsCount}
                </p>
                <p className="text-xs text-slate-500 mt-1">Property configs assigned</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Partner table — fancy but data-dense. Each cell has explicit width
          so nothing overlaps; truncated values fall back to `title` tooltips,
          and project/config columns collapse extras into +N chips. Whole table
          horizontally scrolls inside its card on narrow screens via min-w. */}
      {paginationLoading && isInitialLoad ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
          <Skeleton height={40} />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={56} />
          ))}
        </div>
      ) : (agents || []).length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-flamingo-50 flex items-center justify-center">
            <Handshake size={28} className="text-flamingo-500" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-900">
            {searchInput ? "No partners match your search" : "No channel partners yet"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {searchInput
              ? "Try a different name, email, or RERA number."
              : "Add your first partner to start tracking referrals and conversions."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative">
          {paginationLoading && !isInitialLoad && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-20 flex items-center justify-center">
              <Spinner color="orange" size="lg" />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] border-collapse">
              <colgroup>
                <col style={{ width: "260px" }} />
                <col style={{ width: "220px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "100px" }} />
                {hasPrivilege("canEdit") && <col style={{ width: "70px" }} />}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-5 py-3 font-semibold">Partner</th>
                  <th className="text-left px-4 py-3 font-semibold">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Location</th>
                  <th className="text-left px-4 py-3 font-semibold">Projects</th>
                  <th className="text-left px-4 py-3 font-semibold">Configurations</th>
                  <th className="text-left px-4 py-3 font-semibold">RERA</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  {hasPrivilege("canEdit") && (
                    <th className="text-center px-3 py-3 font-semibold">Edit</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {agents.map((agent) => {
                  const projectList = agent.projects || [];
                  const configList = agent.propertyConfigs || [];
                  const initials = (agent.ownerName || agent.companyName || "?")
                    .split(" ")
                    .map((p) => p[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  const remainingProjectNames = projectList
                    .slice(2)
                    .map((p) => p.projectName)
                    .join(", ");
                  const remainingConfigNames = configList
                    .slice(2)
                    .map((c) => c.configName)
                    .join(", ");
                  const rera = agent.reraNumber || agent.reraNo || agent.rera_number;
                  return (
                    <tr
                      key={agent.customerId}
                      className="group hover:bg-slate-50/60 transition-colors"
                    >
                      {/* Partner — avatar + name + company */}
                      <td className="px-5 py-3 align-middle">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-flamingo-400 to-flamingo-600 text-white flex items-center justify-center font-semibold text-xs shadow-sm shrink-0">
                            {initials || <Handshake size={14} />}
                          </div>
                          <div className="min-w-0">
                            <p
                              className="text-sm font-semibold text-slate-900 truncate"
                              title={agent.ownerName || agent.companyName}
                            >
                              {agent.ownerName || agent.companyName || "Unnamed"}
                            </p>
                            {agent.companyName && agent.ownerName && (
                              <p
                                className="text-xs text-slate-500 truncate"
                                title={agent.companyName}
                              >
                                {agent.companyName}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact — email + phones stacked */}
                      <td className="px-4 py-3 align-middle">
                        <div className="min-w-0 space-y-0.5">
                          {agent.emailId && (
                            <div
                              className="flex items-center gap-1.5 text-xs text-slate-700 truncate"
                              title={agent.emailId}
                            >
                              <Mail size={11} className="text-slate-400 shrink-0" />
                              <span className="truncate">{agent.emailId}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-[11px] text-slate-500">
                            {agent.mobileNoCalling && (
                              <span
                                className="inline-flex items-center gap-1"
                                title={`Phone: ${agent.mobileNoCalling}`}
                              >
                                <Phone size={10} className="text-slate-400" />
                                {agent.mobileNoCalling}
                              </span>
                            )}
                            {agent.mobileNoWa &&
                              agent.mobileNoWa !== agent.mobileNoCalling && (
                                <span
                                  className="inline-flex items-center gap-1"
                                  title={`WhatsApp: ${agent.mobileNoWa}`}
                                >
                                  <MessageSquare
                                    size={10}
                                    className="text-emerald-500"
                                  />
                                  {agent.mobileNoWa}
                                </span>
                              )}
                          </div>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3 align-middle">
                        <div
                          className="flex items-center gap-1.5 text-xs text-slate-700 min-w-0"
                          title={formatLocation(agent.address)}
                        >
                          <MapPin size={11} className="text-slate-400 shrink-0" />
                          <span className="truncate">
                            {formatLocation(agent.address)}
                          </span>
                        </div>
                      </td>

                      {/* Projects — 2 chips + overflow */}
                      <td className="px-4 py-3 align-middle">
                        {projectList.length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-full">
                            {projectList.slice(0, 2).map((p) => (
                              <span
                                key={p.projectId}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 max-w-[140px] truncate"
                                title={p.projectName}
                              >
                                <span className="truncate">{p.projectName}</span>
                              </span>
                            ))}
                            {projectList.length > 2 && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-200 text-slate-700"
                                title={remainingProjectNames}
                              >
                                +{projectList.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Configurations — 2 chips + overflow, flamingo tinted */}
                      <td className="px-4 py-3 align-middle">
                        {configList.length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-full">
                            {configList.slice(0, 2).map((c) => (
                              <span
                                key={c.propertyConfigId}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-flamingo-50 text-flamingo-700 border border-flamingo-100 max-w-[140px] truncate"
                                title={c.configName}
                              >
                                <span className="truncate">{c.configName}</span>
                              </span>
                            ))}
                            {configList.length > 2 && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-flamingo-100 text-flamingo-700"
                                title={remainingConfigNames}
                              >
                                +{configList.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* RERA */}
                      <td className="px-4 py-3 align-middle">
                        <span
                          className="text-xs text-slate-700 tabular-nums truncate block"
                          title={rera || ""}
                        >
                          {rera && String(rera).trim() ? rera : "—"}
                        </span>
                      </td>

                      {/* Status pill */}
                      <td className="px-4 py-3 align-middle">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                            agent.isActive
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-rose-50 text-rose-700 border-rose-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              agent.isActive ? "bg-emerald-500" : "bg-rose-500"
                            }`}
                          />
                          {agent.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      {/* Actions */}
                      {hasPrivilege("canEdit") && (
                        <td className="px-3 py-3 align-middle text-center">
                          <button
                            type="button"
                            onClick={() => openEditModal(agent)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
                            aria-label="Edit partner"
                            title="Edit partner"
                          >
                            <Edit size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination — same numeric-chip strip as the Projects + Approvals pages */}
      {!paginationLoading && !error && pagination.total > 0 && (
        <div className="mt-auto pt-4 border-t border-slate-200">
          <PaginationControls
            pagination={pagination}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            itemName="partners"
            variant="list"
            showInfo={true}
          />
        </div>
      )}

      {hasPrivilege("canEdit") && (
        <FormModal
          open={isFormModalOpen}
          onClose={closeModal}
          title={formMode === "create" ? "Add Partner" : "Edit Partner"}
          size="xl"
        >
            {/* Project / Config multi-select lives outside the form because it
                isn't part of the agent record itself — it captures which
                project/config buckets the agent is assigned to and is merged
                into the request body in handleAddAgentToProject / handleEditAgent. */}
            <div className="relative" ref={dropdownRef}>
              <div className="flex gap-4">
                <div className="relative flex-1">
                  {/* Dropdown Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent closing the modal immediately
                      setDropdownOpen(!dropdownOpen);
                    }}
                    className="w-full px-2 py-2 flex items-center justify-between text-left border border-slate-300 rounded-md focus:outline-none text-sm focus:ring-1 focus:ring-flamingo-500 mb-2"
                  >
                    {selectedProjectIds.length > 0
                      ? `${selectedProjectIds.length} Projects Selected`
                      : "Select Projects"}
                    <ChevronDown
                      className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : "rotate-0"
                        }`}
                    />
                  </button>

                  {/* Dropdown Content */}
                  {dropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md text-sm shadow-lg">
                      {projects.map((project) => (
                        <label
                          key={project.projectId}
                          className="block px-4 py-2 cursor-pointer hover:bg-gray-100"
                        >

                          <input
                            type="checkbox"
                            value={project.projectId}
                            checked={selectedProjectIds.includes(
                              project.projectId
                            )}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent closing the modal immediately
                              handleProjectSelect(
                                project.projectId,
                                e.target.checked
                              );
                            }}
                            className="mr-2"
                          />
                          {project.projectName}

                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Multi-Select Checkbox for Configurations - Conditionally Rendered based on selectedProjectIds */}
                {selectedProjectIds.length > 0 &&
                  <div className="flex-1">
                    {selectedProjectIds.map((projectId) => (
                      <div key={projectId} className="mb-4">
                        <span className="font-medium">
                          Configurations for - {
                            projects.find((p) => p.projectId === projectId)
                              ?.projectName
                          }
                        </span>

                        {availableConfigs[projectId] && Array.isArray(availableConfigs[projectId]) ? (
                          availableConfigs[projectId].map((config) => (
                            <div
                              key={config.propertyConfigId}
                              className="mb-2"
                            >
                              <label
                                htmlFor={`config-${config.propertyConfigId}`}
                                className="flex items-center"
                              >
                                <input
                                  type="checkbox"
                                  id={`config-${config.propertyConfigId}`}
                                  value={config.propertyConfigId}
                                  checked={
                                    selectedProjectConfigs[projectId]?.includes(
                                      config.propertyConfigId
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    e.stopPropagation(); // Prevent closing the modal immediately
                                    handleConfigSelect(
                                      projectId,
                                      config.propertyConfigId,
                                      e.target.checked
                                    );
                                  }}
                                  className="mr-2"
                                />
                                {config.configName}
                              </label>
                            </div>
                          ))
                        ) : (
                          <Typography variant="body2" color="gray">
                            No configurations available for this project.
                          </Typography>
                        )}
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(
                  formMode === "create" ? handleAddAgentToProject : handleEditAgent
                )}
                className="space-y-4"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3">
                  <Field name="companyName" label="Company Name" required />
                  <Field name="ownerName" label="Owner Name" required />
                  <Field name="mobileNoWa" label="Whatsapp Number" type="phone" required />
                  <Field name="mobileNoCalling" label="Calling Number" type="phone" required />
                  <Field
                    className="md:col-span-2"
                    name="emailId"
                    label="Email"
                    type="email"
                    required
                  />

                  <div className="col-span-2 my-2">
                    <h4 className="font-medium mb-2">Address</h4>
                    <AddressFields prefix="address" required={[
                      "buildingNo", "streetName1", "city", "district", "state", "pinCode", "country",
                    ]} />
                  </div>

                  <Field
                    className="md:col-span-2"
                    name="reraNumber"
                    label="RERA Number"
                    required
                  />
                </div>

                <FormSubmit loadingLabel={formMode === "create" ? "Submitting..." : "Saving..."}>
                  {formMode === "create" ? "Submit" : "Save"}
                </FormSubmit>
              </form>
            </FormProvider>
          </FormModal>
      )}
      
      {/* Bulk Upload Agents Modal */}
      <BulkUploadAgents
        isOpen={isBulkUploadModalOpen}
        onClose={closeBulkUploadModal}
        onUploadSuccess={() => {
          refetch();
        }}
        projectId={null}
      />
    </div>
  );
}

export default AgentList;